import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Check, Clock, AlertTriangle, MapPin,
  Loader2, Plus, Lock, RotateCcw, Users,
} from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';
import { Employee, Job } from '../../types/backend.types';
import { TimeEntryRow, PendingCorrection, EmployeeReviewStatus, formatHM } from './timecard.types';
import { EditEntryModal } from './EditEntryModal';
import { CorrectionsReview } from './CorrectionsReview';
import { CrewHoursModal } from './CrewHoursModal';
import { easternNow, isoToEasternDate } from '../../utils/easternTime';

const pad = (n: number) => String(n).padStart(2, '0');
// Eastern calendar date ('YYYY-MM-DD') for a timestamp, so entries group onto the
// correct Eastern day regardless of the admin's own timezone.
const localDateStr = (input: string | Date) =>
  isoToEasternDate(typeof input === 'string' ? input : input.toISOString());
// Plain YYYY-MM-DD of a Date in the viewer's OWN local calendar — no timezone
// conversion. getWeekPeriod() builds friday/thursday as local calendar dates, so
// the pay-week bounds must be read back the same way. (Using localDateStr() here
// converted thursday's 23:59:59 local to Eastern, rolling it to the next day for
// Central/Mountain/Pacific admins and sweeping today's open clock-ins into the
// just-ended week — every clocked-in employee showed a phantom "1 open".)
const ymdLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' }) : '—';
const isForeman = (e: Employee) => e.role === 'foreman' || e.role === 'asphalt_foreman';
const nameOf = (e: Employee) => `${e.first_name} ${e.last_name}`;

// Friday→Thursday week, offsetWeeks relative to the current week
// (0 = current week, -1 = last fully-completed pay period).
const getWeekPeriod = (offsetWeeks: number) => {
  const today = easternNow(); // anchor the Fri->Thu week to the Eastern calendar
  const currentDay = today.getDay();
  let daysSinceLastFriday: number;
  if (currentDay === 0) daysSinceLastFriday = 2;
  else if (currentDay >= 1 && currentDay <= 4) daysSinceLastFriday = currentDay + 2;
  else daysSinceLastFriday = currentDay - 5;

  const friday = new Date(today);
  friday.setDate(today.getDate() - daysSinceLastFriday + offsetWeeks * 7);
  friday.setHours(0, 0, 0, 0);

  const thursday = new Date(friday);
  thursday.setDate(friday.getDate() + 6);
  thursday.setHours(23, 59, 59, 999);
  return { friday, thursday };
};

interface TimeCardEditorProps {
  // 'approve' = last completed pay period with approval + employee corrections;
  // 'manage'  = current week, edit-only (no approval, no corrections).
  mode?: 'approve' | 'manage';
}

export const TimeCardEditor: React.FC<TimeCardEditorProps> = ({ mode = 'approve' }) => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const isApprove = mode === 'approve';

  const { friday, thursday } = useMemo(() => getWeekPeriod(isApprove ? -1 : 0), [isApprove]);
  const weekStartISO = useMemo(() => friday.toISOString(), [friday]);
  const fridayDateStr = useMemo(() => ymdLocal(friday), [friday]);
  const thursdayDateStr = useMemo(() => ymdLocal(thursday), [thursday]);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [entries, setEntries] = useState<TimeEntryRow[]>([]);
  const [approvedIds, setApprovedIds] = useState<Set<number>>(new Set());
  const [corrections, setCorrections] = useState<PendingCorrection[]>([]);
  // Per-employee review status (review_status + full corrections), fetched lazily
  // per card. undefined = not yet loaded; null = employee never opened their review.
  const [statusCache, setStatusCache] = useState<Record<number, EmployeeReviewStatus | null>>({});
  const [statusLoadingId, setStatusLoadingId] = useState<number | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [actionBusy, setActionBusy] = useState(false);

  const [modal, setModal] = useState<{ entry: TimeEntryRow | null; date: string } | null>(null);
  const [showCrew, setShowCrew] = useState(false);
  const [sortMode, setSortMode] = useState<'crew' | 'name'>('crew');
  const keepEmpRef = useRef<number | null>(null);

  const adminName = `${auth?.user?.first_name ?? ''} ${auth?.user?.last_name ?? ''}`.trim();
  const isAdmin = auth?.user?.role === 'admin';

  // ---- data loading -------------------------------------------------------
  const reloadWeek = async () => {
    const [entriesRes, approvalsRes, pendingRes] = await Promise.all([
      api.get(`${API_CONFIG.ENDPOINTS.TIME_ENTRIES}?start_date=${fridayDateStr}&end_date=${thursdayDateStr}`),
      isApprove ? api.get(`${API_CONFIG.ENDPOINTS.WEEK_APPROVALS_ALL}?week_start=${encodeURIComponent(weekStartISO)}`) : null,
      isApprove ? api.get(API_CONFIG.ENDPOINTS.TIMECARD_PENDING).catch(() => ({ data: { success: false, data: [] } })) : null,
    ]);
    if (entriesRes.data.success) setEntries(entriesRes.data.data || []);
    if (approvalsRes?.data?.success) setApprovedIds(new Set<number>(approvalsRes.data.data || []));
    if (pendingRes?.data?.success) {
      const all: PendingCorrection[] = pendingRes.data.data || [];
      // keep only this pay period's pending modifications
      setCorrections(all.filter((c) => !c.week_start_date || localDateStr(c.week_start_date) === fridayDateStr));
    }
  };

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [empRes, jobRes] = await Promise.all([
        api.get(API_CONFIG.ENDPOINTS.EMPLOYEES),
        api.get(API_CONFIG.ENDPOINTS.JOBS),
      ]);
      if (empRes.data.success) setEmployees(empRes.data.data || []);
      if (jobRes.data.success) setJobs(jobRes.data.data || []);
      await reloadWeek();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load time cards.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch a single employee's Friday-review status + full corrections list.
  const loadStatus = async (employeeId: number, force = false) => {
    if (!force && employeeId in statusCache) return;
    setStatusLoadingId(employeeId);
    try {
      const res = await api.get(
        `${API_CONFIG.ENDPOINTS.TIMECARD_EMPLOYEE_STATUS}?employee_id=${employeeId}&week_start=${fridayDateStr}`,
      );
      const data: EmployeeReviewStatus | null = res.data?.success ? res.data.data : null;
      setStatusCache((prev) => ({ ...prev, [employeeId]: data }));
    } catch {
      setStatusCache((prev) => ({ ...prev, [employeeId]: null }));
    } finally {
      setStatusLoadingId((id) => (id === employeeId ? null : id));
    }
  };

  useEffect(() => {
    if (auth?.user?.employee_id) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.employee_id]);

  // Quiet refresh when the PWA returns to the foreground (stays mounted across backgrounding).
  useEffect(() => {
    const refresh = () => { if (document.visibilityState !== 'hidden') reloadWeek().catch(() => {}); };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const empById = useMemo(() => {
    const m = new Map<number, Employee>();
    employees.forEach((e) => m.set(e.employee_id, e));
    return m;
  }, [employees]);

  // The foreman whose crew this employee belongs to (themselves if they ARE a foreman).
  const foremanOf = (e: Employee) => {
    const fId = isForeman(e) ? e.employee_id : e.crew_foreman_id;
    return fId ? empById.get(fId) : undefined;
  };
  const crewLabel = (e: Employee) => {
    const f = foremanOf(e);
    return f ? nameOf(f) : 'Unassigned';
  };

  // ---- roster (only employees with time / pending mods this period) -------
  const roster = useMemo(() => {
    const ids = new Set<number>();
    entries.forEach((e) => ids.add(e.employee_id));
    corrections.forEach((c) => ids.add(c.employee_id));
    const list = employees.filter((e) => ids.has(e.employee_id));

    if (sortMode === 'name') {
      return list.sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
    }
    // crew mode: group under the foreman, foreman first, then members A–Z; unassigned last
    const crewKey = (e: Employee) => {
      const f = foremanOf(e);
      return f ? nameOf(f) : '￿';
    };
    return list.sort((a, b) => {
      const ka = crewKey(a), kb = crewKey(b);
      if (ka !== kb) return ka.localeCompare(kb);
      const fa = isForeman(a) ? 0 : 1, fb = isForeman(b) ? 0 : 1;
      if (fa !== fb) return fa - fb;
      return nameOf(a).localeCompare(nameOf(b));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, entries, corrections, sortMode, empById]);

  useEffect(() => {
    if (currentIndex > roster.length - 1) setCurrentIndex(Math.max(0, roster.length - 1));
  }, [roster.length, currentIndex]);

  // Keep the same employee in view when the sort order changes.
  useEffect(() => {
    const id = keepEmpRef.current;
    if (id == null) return;
    const idx = roster.findIndex((r) => r.employee_id === id);
    if (idx >= 0) setCurrentIndex(idx);
    keepEmpRef.current = null;
  }, [sortMode, roster]);

  // Jump back to the top of the card when moving to another employee.
  useEffect(() => {
    const mc = document.querySelector('.main-content');
    if (mc) mc.scrollTo({ top: 0 });
    else window.scrollTo({ top: 0 });
  }, [currentIndex]);


  const approvedCount = useMemo(
    () => roster.filter((e) => approvedIds.has(e.employee_id)).length,
    [roster, approvedIds],
  );

  const current = roster[currentIndex];

  // Load the current employee's review status whenever the card changes.
  // Both modes: My Time change requests can target the current week too, so
  // manage mode also needs the corrections (with Accept/Deny) visible.
  useEffect(() => {
    if (current?.employee_id) loadStatus(current.employee_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.employee_id]);

  const currentStatus = current ? statusCache[current.employee_id] : undefined;
  const statusLoaded = current ? current.employee_id in statusCache : false;
  const statusLoading = !!current && statusLoadingId === current.employee_id;

  // ---- per-employee derived data -----------------------------------------
  const view = useMemo(() => {
    if (!current) return null;
    const empEntries = entries
      .filter((e) => e.employee_id === current.employee_id)
      .sort((a, b) => new Date(a.clock_in).getTime() - new Date(b.clock_in).getTime());
    const unclosed = empEntries.filter((e) => !e.clock_out);
    const flagged = empEntries.filter((e) => e.needs_review);
    const geoIssues = empEntries.filter(
      (e) => e.geofence_status === 'unknown_job' || e.geofence_status === 'outside',
    );
    const totalHours = empEntries.reduce((s, e) => s + (Number(e.hours_worked) || 0), 0);

    // 7 day buckets Fri→Thu
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(friday);
      d.setDate(friday.getDate() + i);
      const key = localDateStr(d);
      return { date: d, key, entries: empEntries.filter((e) => localDateStr(e.clock_in) === key) };
    });

    const approved = approvedIds.has(current.employee_id);
    return { empEntries, unclosed, flagged, geoIssues, totalHours, days, approved };
  }, [current, entries, approvedIds, friday]);

  // Pending modifications for the gate/hint: prefer the rich per-employee status
  // (all pending rows, minus auto-fix entries); fall back to the global /pending
  // list until that status has loaded.
  const pendingModCount = (() => {
    if (!current) return 0;
    if (statusLoaded) {
      return (currentStatus?.corrections || []).filter(
        (c) => c.admin_status === 'pending' && !/auto-?adjust|auto-?ajustad/i.test(c.employee_notes || ''),
      ).length;
    }
    return corrections.filter((c) => c.employee_id === current.employee_id).length;
  })();

  const canApprove = !!view && view.unclosed.length === 0 && pendingModCount === 0;

  const afterCorrectionResolved = async () => {
    await reloadWeek();
    if (current) await loadStatus(current.employee_id, true);
  };

  // ---- actions ------------------------------------------------------------
  const approveWeek = async () => {
    if (!current || !view) return;
    setActionBusy(true);
    setError(null);
    try {
      const res = await api.post(API_CONFIG.ENDPOINTS.APPROVE_WEEK, {
        employee_id: current.employee_id,
        week_start: weekStartISO,
      });
      if (!res.data.success) throw new Error(res.data.error || 'Failed to approve');
      setApprovedIds((prev) => new Set(prev).add(current.employee_id));
      await reloadWeek();
      // auto-advance to the next not-yet-approved card
      const nextUnapproved = roster.findIndex(
        (e, i) => i > currentIndex && !approvedIds.has(e.employee_id) && e.employee_id !== current.employee_id,
      );
      if (nextUnapproved !== -1) setCurrentIndex(nextUnapproved);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to approve week.');
    } finally {
      setActionBusy(false);
    }
  };

  const unapproveWeek = async () => {
    if (!current) return;
    setActionBusy(true);
    setError(null);
    try {
      const res = await api.post(API_CONFIG.ENDPOINTS.UNAPPROVE_WEEK, {
        employee_id: current.employee_id,
        week_start: weekStartISO,
      });
      if (!res.data.success) throw new Error(res.data.error || 'Failed to unapprove');
      setApprovedIds((prev) => {
        const next = new Set(prev);
        next.delete(current.employee_id);
        return next;
      });
      await reloadWeek();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to unapprove week.');
    } finally {
      setActionBusy(false);
    }
  };

  const go = (dir: -1 | 1) => {
    setCurrentIndex((i) => Math.min(roster.length - 1, Math.max(0, i + dir)));
  };

  // ---- swipe --------------------------------------------------------------
  const touch = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) go(dx < 0 ? 1 : -1);
    touch.current = null;
  };

  // ---- render -------------------------------------------------------------
  const spin = <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}.spin{animation:spin 1s linear infinite}`}</style>;

  if (!isAdmin) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Lock size={40} color="#94a3b8" style={{ marginBottom: 12 }} />
        <h2 style={{ color: '#1f2937' }}>Admins only</h2>
        <p style={{ color: '#64748b' }}>{isApprove ? 'Time card approval' : 'Current week time'} is restricted to administrators.</p>
        <button onClick={() => navigate('/')} style={{ marginTop: 16, padding: '10px 18px', borderRadius: 8, border: 'none', background: '#667eea', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
          Back to dashboard
        </button>
      </div>
    );
  }

  const weekLabel = `${friday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${thursday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  return (
    <div style={{ background: '#f1f5f9', minHeight: '100vh', paddingBottom: 40 }}>
      {spin}

      {/* Sticky header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1f2937' }}>{isApprove ? 'Approve Time Cards' : 'Current Week Time'}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>{isApprove ? `Pay period ${weekLabel}` : `This week · ${weekLabel}`}</div>
          </div>
          <button onClick={() => reloadWeek()} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }} title="Refresh">
            <RotateCcw size={18} color="#94a3b8" />
          </button>
        </div>
        {/* progress + sort */}
        {roster.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                {isApprove
                  ? `${approvedCount} of ${roster.length} approved · ${currentIndex + 1}/${roster.length}`
                  : `${roster.length} ${roster.length === 1 ? 'person' : 'people'} · ${currentIndex + 1}/${roster.length}`}
              </span>
              <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 16, padding: 2 }}>
                {(['crew', 'name'] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => { if (current && sortMode !== m) { keepEmpRef.current = current.employee_id; setSortMode(m); } }}
                    style={{
                      padding: '4px 12px', borderRadius: 14, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none',
                      background: sortMode === m ? '#fff' : 'transparent', color: sortMode === m ? '#4338ca' : '#94a3b8',
                      boxShadow: sortMode === m ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                    }}
                  >
                    {m === 'crew' ? 'Crew' : 'A–Z'}
                  </button>
                ))}
              </div>
            </div>
            {isApprove && (
              <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${roster.length ? (approvedCount / roster.length) * 100 : 0}%`, background: '#16a34a', transition: 'width .3s' }} />
              </div>
            )}
          </div>
        )}
        {/* Pinned employee picker + arrows */}
        {current && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <button onClick={() => go(-1)} disabled={currentIndex === 0} style={navBtn(currentIndex === 0)}>
              <ChevronLeft size={22} />
            </button>
            <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
              <div style={{ padding: '12px 30px 12px 10px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 16, fontWeight: 600, background: '#fff', color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {approvedIds.has(current.employee_id) ? '🟢 ' : ''}{current.first_name} {current.last_name}
              </div>
              <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#94a3b8', fontSize: 12 }}>▾</span>
              <select
                value={current.employee_id}
                onChange={(e) => setCurrentIndex(roster.findIndex((r) => r.employee_id === Number(e.target.value)))}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, border: 'none', WebkitAppearance: 'none', appearance: 'none', background: 'transparent' }}
              >
              {roster.map((e) => {
                const member = sortMode === 'crew' && !isForeman(e) && !!e.crew_foreman_id && empById.has(e.crew_foreman_id);
                return (
                  <option key={e.employee_id} value={e.employee_id}>
                    {member ? '\u2002\u2002\u2002' : ''}{approvedIds.has(e.employee_id) ? '🟢 ' : ''}{e.first_name} {e.last_name}
                  </option>
                );
              })}
              </select>
            </div>
            <button onClick={() => go(1)} disabled={currentIndex === roster.length - 1} style={navBtn(currentIndex === roster.length - 1)}>
              <ChevronRight size={22} />
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 60, color: '#64748b' }}>
          <Loader2 size={32} className="spin" />
          <div style={{ marginTop: 12 }}>Loading time cards…</div>
        </div>
      ) : error ? (
        <div style={{ margin: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 16, color: '#dc2626' }}>
          {error}
          <button onClick={loadAll} style={{ display: 'block', marginTop: 10, padding: '8px 14px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Retry</button>
        </div>
      ) : roster.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>
          <Clock size={40} color="#94a3b8" style={{ marginBottom: 12 }} />
          <div style={{ fontWeight: 600, color: '#1f2937' }}>No time this pay period</div>
          <div style={{ fontSize: 14 }}>Nobody clocked in during {weekLabel}.</div>
        </div>
      ) : current && view ? (
        <>
          {/* The swipeable card */}
          <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} style={{ padding: '12px 16px 0' }}>
            <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              {/* Card header */}
              <div style={{ padding: 16, borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#1f2937' }}>
                      {current.first_name} {current.last_name}
                    </div>
                    <div style={{ fontSize: 13, color: '#64748b', textTransform: 'capitalize' }}>
                      {current.role?.replace(/_/g, ' ')}
                    </div>
                    {!isForeman(current) && current.crew_foreman_id && (
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                        Crew: {crewLabel(current)}
                      </div>
                    )}
                    {/* Employee self-review status under the name — quick scan while
                        flipping cards (full detail still shown in the banner below). */}
                    {isApprove && !(statusLoading && !statusLoaded) && (() => {
                      const rs = currentStatus?.review_status;
                      // Submitting corrections IS the employee's review (reviewed_at is
                      // stamped then) — once the admin settles them, count it as
                      // reviewed; never ask the employee to approve again.
                      const stillPending = (currentStatus?.corrections || []).some((c) => c.admin_status === 'pending');
                      const s = rs === 'approved'
                        ? { fg: '#166534', icon: <Check size={14} />, text: 'Employee approved their time' }
                        : rs === 'resolved' || (rs === 'issues_flagged' && !stillPending)
                          ? { fg: '#166534', icon: <Check size={14} />, text: 'Employee reviewed · corrections resolved' }
                          : rs === 'issues_flagged'
                            ? { fg: '#b45309', icon: <AlertTriangle size={14} />, text: 'Employee flagged issues' }
                            : rs === 'skipped'
                              ? { fg: '#64748b', icon: <Clock size={14} />, text: 'Employee skipped review' }
                              : { fg: '#64748b', icon: <Clock size={14} />, text: 'Awaiting employee review' };
                      return (
                        <div style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: s.fg }}>
                          {s.icon} {s.text}
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: '#1f2937' }}>{formatHM(view.totalHours)}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>h:mm{view.totalHours > 40 ? ' · OT' : ''}</div>
                  </div>
                </div>
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {view.approved && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#dcfce7', color: '#166534', borderRadius: 20, padding: '4px 12px', fontSize: 13, fontWeight: 700 }}>
                      <Check size={15} /> Approved
                    </div>
                  )}
                  <button
                    onClick={() => setShowCrew(true)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eef2ff', color: '#4338ca', border: 'none', borderRadius: 20, padding: '5px 12px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                  >
                    <Users size={15} /> Crew hours
                  </button>
                </div>
              </div>

              {/* Issues strip */}
              {(view.unclosed.length > 0 || view.flagged.length > 0 || view.geoIssues.length > 0) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '12px 16px', background: '#fffbeb', borderBottom: '1px solid #fde68a' }}>
                  {view.unclosed.length > 0 && <IssuePill icon={<Clock size={13} />} color="#ea580c" text={`${view.unclosed.length} open`} />}
                  {view.flagged.length > 0 && <IssuePill icon={<AlertTriangle size={13} />} color="#d97706" text={`${view.flagged.length} flagged`} />}
                  {view.geoIssues.length > 0 && <IssuePill icon={<MapPin size={13} />} color="#dc2626" text={`${view.geoIssues.length} location`} />}
                </div>
              )}

              {/* Day list — individual entry rows (tap to edit times / job / lunch) */}
              <div style={{ padding: '4px 0' }}>
                {view.days.map((day) => (
                  <div key={day.key} style={{ padding: '8px 16px', borderBottom: '1px solid #f8fafc' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                      {day.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </div>
                    {day.entries.map((e) => {
                      const isOpen = !e.clock_out;
                      const flag = e.needs_review;
                      const geo = e.geofence_status === 'unknown_job' || e.geofence_status === 'outside';
                      const accent = isOpen ? '#ea580c' : geo ? '#dc2626' : flag ? '#d97706' : 'transparent';
                      const overnight = !!e.clock_out && localDateStr(e.clock_out) > day.key;
                      return (
                        <button
                          key={e.entry_id}
                          onClick={() => setModal({ entry: e, date: day.key })}
                          style={{
                            width: '100%', textAlign: 'left', background: '#f8fafc', border: '1px solid #eef2f7',
                            borderLeft: `4px solid ${accent}`, borderRadius: 8, padding: '10px 12px', marginBottom: 6,
                            cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937' }}>
                              {fmtTime(e.clock_in)} – {isOpen ? <span style={{ color: '#ea580c' }}>open</span> : `${fmtTime(e.clock_out)}${overnight ? ' +1' : ''}`}
                            </div>
                            <div style={{ fontSize: 13, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {e.job_name || jobs.find((j) => j.job_id === e.job_id)?.job_name || `Job #${e.job_id}`}
                              {e.lunch_taken ? ` · lunch ${e.lunch_minutes ?? 30}m` : ''}
                            </div>
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', whiteSpace: 'nowrap' }}>
                            {e.clock_out ? formatHM(Number(e.hours_worked) || 0) : ''}
                          </div>
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setModal({ entry: null, date: day.key })}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#a5b4fc', fontSize: 13, padding: '2px 0' }}
                    >
                      <Plus size={14} /> Add entry
                    </button>
                  </div>
                ))}
              </div>

              {/* Employee review status + modifications. Approve mode always shows
                  the review banner; manage mode only when this week actually has
                  corrections (Deny must be reachable wherever they show). */}
              {(isApprove || (currentStatus?.corrections?.length ?? 0) > 0) && (
                <div style={{ padding: '0 16px 16px' }}>
                  <CorrectionsReview
                    status={currentStatus ?? null}
                    loading={statusLoading && !statusLoaded}
                    onResolved={afterCorrectionResolved}
                    crewEntries={(jobId, ymd) => entries.filter((e) => e.job_id === jobId && localDateStr(e.clock_in) === ymd)}
                    employeeId={current?.employee_id ?? null}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Approve footer (approve mode only) */}
          {isApprove && (
            <div style={{ padding: 16 }}>
              {view.approved ? (
                <button onClick={unapproveWeek} disabled={actionBusy} style={footerBtn('#fff', '#475569', '1px solid #e5e7eb')}>
                  {actionBusy ? <Loader2 size={18} className="spin" /> : <RotateCcw size={18} />} Unapprove this week
                </button>
              ) : (
                <>
                  <button onClick={approveWeek} disabled={actionBusy || !canApprove} style={footerBtn('#16a34a', '#fff', 'none', !canApprove)}>
                    {actionBusy ? <Loader2 size={18} className="spin" /> : <Check size={18} />} Approve week
                  </button>
                  {!canApprove && (
                    <div style={{ textAlign: 'center', fontSize: 13, color: '#b45309', marginTop: 8 }}>
                      {view.unclosed.length > 0 && `Close ${view.unclosed.length} open ${view.unclosed.length === 1 ? 'entry' : 'entries'}. `}
                      {pendingModCount > 0 && `Resolve ${pendingModCount} pending ${pendingModCount === 1 ? 'modification' : 'modifications'}.`}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      ) : null}

      {modal && current && (
        <EditEntryModal
          employeeId={current.employee_id}
          employeeName={`${current.first_name} ${current.last_name}`}
          entry={modal.entry}
          defaultDate={modal.date}
          jobs={jobs}
          adminName={adminName}
          onClose={() => setModal(null)}
          onSaved={afterCorrectionResolved}
        />
      )}


      {showCrew && current && (
        <CrewHoursModal
          current={current}
          employees={employees}
          entries={entries}
          jumpableIds={new Set(roster.map((r) => r.employee_id))}
          weekLabel={weekLabel}
          onJump={(id) => {
            const idx = roster.findIndex((r) => r.employee_id === id);
            if (idx !== -1) setCurrentIndex(idx);
            setShowCrew(false);
          }}
          onClose={() => setShowCrew(false)}
        />
      )}
    </div>
  );
};

// ---- small style helpers --------------------------------------------------
const navBtn = (disabled: boolean): React.CSSProperties => ({
  width: 44, height: 44, borderRadius: 10, border: '1px solid #e5e7eb',
  background: '#fff', color: disabled ? '#cbd5e1' : '#475569',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: disabled ? 'default' : 'pointer', flexShrink: 0,
});

const footerBtn = (bg: string, color: string, border: string, disabled = false): React.CSSProperties => ({
  width: '100%', padding: 16, borderRadius: 12, border, background: bg, color,
  fontSize: 17, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  opacity: disabled ? 0.5 : 1,
});

const IssuePill: React.FC<{ icon: React.ReactNode; color: string; text: string }> = ({ icon, color, text }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fff', border: `1px solid ${color}40`, color, borderRadius: 16, padding: '4px 10px', fontSize: 12, fontWeight: 700 }}>
    {icon}{text}
  </span>
);
